import { getApiUrl } from '../config/api';

export interface Dealer {
  id: string;
  name: string;
  shop_name: string;
  shop_address: string;
  phone: string;
  created_at: string;
  updated_at: string;
  // Enhanced fields for dealer statistics
  totalBusiness?: number;
  outstandingBalance?: number;
  totalOrders?: number;
  lastOrderDate?: string;
  averageOrderValue?: number;
  paymentStatus?: 'Good' | 'Overdue' | 'New';
}

export interface CreateDealerData {
  name: string;
  shop_name?: string;
  shop_address?: string;
  phone: string;
}

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('ihub_token') || sessionStorage.getItem('ihub_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export const dealerService = {
  // Create a new dealer
  async createDealer(dealerData: CreateDealerData): Promise<{ data: Dealer | null; error: any }> {
    try {
      console.log('Dealer service: Creating dealer with data:', dealerData);
      console.log('Dealer service: API URL:', getApiUrl('dealers'));
      console.log('Dealer service: Headers:', getAuthHeaders());
      
      const response = await fetch(getApiUrl('dealers'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(dealerData)
      });

      console.log('Dealer service: Response status:', response.status);
      console.log('Dealer service: Response ok:', response.ok);

      const result = await response.json();
      console.log('Dealer service: Response data:', result);

      if (!response.ok) {
        console.error('Dealer service: API error:', result);
        return { data: null, error: result.error || `HTTP ${response.status}: Failed to create dealer` };
      }

      return { data: result.dealer, error: null };
    } catch (error) {
      console.error('Dealer service: Network error:', error);
      return { data: null, error: `Network error: ${error instanceof Error ? error.message : 'Unknown error'}` };
    }
  },

  // Get all dealers with optional enhanced statistics
  async getAllDealers(includeStats: boolean = false): Promise<{ data: Dealer[] | null; error: any }> {
    try {
      const url = includeStats 
        ? getApiUrl('dealers?include_stats=true') 
        : getApiUrl('dealers');
        
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const result = await response.json();

      if (!response.ok) {
        return { data: null, error: result.error || 'Failed to fetch dealers' };
      }

      return { data: result.dealers, error: null };
    } catch (error) {
      console.error('Error fetching dealers:', error);
      return { data: null, error: 'Network error' };
    }
  },

  // Get dealer by ID
  async getDealerById(id: string): Promise<{ data: Dealer | null; error: any }> {
    try {
      const response = await fetch(getApiUrl(`dealers?id=${id}`), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const result = await response.json();

      if (!response.ok) {
        return { data: null, error: result.error || 'Failed to fetch dealer' };
      }

      // Find the dealer from the returned array
      const dealer = result.dealers?.find((d: Dealer) => d.id === id);
      return { data: dealer || null, error: dealer ? null : 'Dealer not found' };
    } catch (error) {
      console.error('Error fetching dealer:', error);
      return { data: null, error: 'Network error' };
    }
  },

  // Search dealers by name or phone
  async searchDealers(name?: string, phone?: string): Promise<{ data: Dealer[] | null; error: any }> {
    try {
      const params = new URLSearchParams();
      if (name) params.append('name', name);
      if (phone) params.append('phone', phone);
      
      const response = await fetch(getApiUrl(`dealers/search?${params.toString()}`), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const result = await response.json();

      if (!response.ok) {
        return { data: null, error: result.error || 'Failed to search dealers' };
      }

      return { data: result.dealers, error: null };
    } catch (error) {
      console.error('Error searching dealers:', error);
      return { data: null, error: 'Network error' };
    }
  },

  // Update dealer
  async updateDealer(id: string, dealerData: Partial<CreateDealerData>): Promise<{ data: Dealer | null; error: any }> {
    try {
      const response = await fetch(getApiUrl('dealers'), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ id, ...dealerData })
      });

      const result = await response.json();

      if (!response.ok) {
        return { data: null, error: result.error || 'Failed to update dealer' };
      }

      return { data: result.dealer, error: null };
    } catch (error) {
      console.error('Error updating dealer:', error);
      return { data: null, error: 'Network error' };
    }
  },

  // Delete dealer
  async deleteDealer(id: string): Promise<{ error: any }> {
    try {
      const deleteUrl = getApiUrl(`dealers?id=${id}`);
      console.log('🗑️ Dealer service: Deleting dealer with ID:', id);
      console.log('🗑️ Dealer service: DELETE URL:', deleteUrl);
      console.log('🗑️ Dealer service: Full URL breakdown:', {
        baseUrl: getApiUrl(''),
        endpoint: `dealers?id=${id}`,
        fullUrl: deleteUrl
      });
      console.log('🗑️ Dealer service: Headers:', getAuthHeaders());

      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      console.log('🗑️ Dealer service: Response status:', response.status);
      console.log('🗑️ Dealer service: Response ok:', response.ok);

      const result = await response.json();
      console.log('🗑️ Dealer service: Response data:', result);

      if (!response.ok) {
        console.error('🚫 Dealer service: Delete failed:', result);
        return { error: result.error || 'Failed to delete dealer' };
      }

      console.log('✅ Dealer service: Delete successful');
      return { error: null };
    } catch (error) {
      console.error('🚫 Dealer service: Network error:', error);
      return { error: 'Network error' };
    }
  }
};